import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useStore } from 'react-context-hook';

// Error handler
import useErrorHandler from '../utils/useErrorHandler';

// Services
import LocalStorageService from '../services/LocalStorageService';
import ReportService from '../services/ReportService';
import OrganisationTagService from '../services/OrganisationTagService';

// Grommet
import { ResponsiveContext } from 'grommet'

// Appt Components
import Anchor from '../components/simple/anchor/Anchor';
import Box from '../components/simple/box/Box';
import Text from '../components/simple/text/Text';


// Appt Widgets
import moment from 'moment';
import BreadcrumbBar from './shared/BreadcrumbBar';
import ActionButton from '../components/compound/actionButton/ActionButton';
import DashboardStats from './Dashboard-Stats';

function Dashboard(props) {
  const navigate = useNavigate();
  const { error, showError } = useErrorHandler(null);

  const [apiToken, setApiToken, deleteApiToken] = useStore();
  const [currentUser, setCurrentUser, deleteCurrentUser] = useStore({});
  const [currentOrgs, setCurrentOrgs, deleteCurrentOrgs] = useStore([]);

  const [collaboratingReports, setCollaboringReports] = useState([]);
  const [allGroups, setAllGroups] = useState([])

  const terms = LocalStorageService.get('terms');
  const activeUser = LocalStorageService.get('userDetails')
  const activeOrg = LocalStorageService.get('activeOrg');

  const [dashboardState, setDashboardState] = useState({
    systemUsers: '',
    students: '',
    smartplans: '',
    incidents: '',
  });


  const getCollaboratingReports = async () => {

    if (!activeOrg) return false

    const body = {
      submissionType: 'incidentreport',
      query: [],
      reports: ['incidentsAsCollaborator']
    }

    const params = {
      orgId: activeOrg
    }

    const results = await ReportService.getReport(apiToken, params, body)

    if (results?.incidentsAsCollaborator?.length) {
      // process collaboratorsFinished
      results.incidentsAsCollaborator = results.incidentsAsCollaborator.map((report) => {
        try {
          if (report?.collaboratorsFinished) {
            report.collaboratorsFinished = JSON.parse(report.collaboratorsFinished)
          }
        } catch (err) {
          console.error('Error processing report', report, err)
        }
        return report
      })

      // filter out reports where the current user has finished collaborating
      results.incidentsAsCollaborator = results.incidentsAsCollaborator.filter((report) => {
        // if collaboratorsFinished is not set, return true
        if (!report.collaboratorsFinished?.length) return true;
        // if collaboratorsFinished is set, check if the current user is in it

        if (report.collaboratorsFinished.includes(activeUser.id)) {
          // if the current user is in the collaboratorsFinished, return false
          return false;
        }
        // otherwise, return true
        return true;
      });


      // sort by reference descending
      results.incidentsAsCollaborator.sort((a, b) => {
        if (a.reference < b.reference) {
          return 1; // b comes before a
        }
        if (a.reference > b.reference) {
          return -1; // a comes before b
        }
        return 0; // they are equal
      });


      setCollaboringReports(results.incidentsAsCollaborator)
      // only return the last 50 report
      const reports = results.incidentsAsCollaborator.slice(0, 50)
      return reports
    }

    return []

  }
  useEffect(() => {
    getCollaboratingReports()
    getGroups()
  }, [props.activeOrg])



  const getGroups = async () => {
    // Now find all groups
    const parsedAcademicYears = await OrganisationTagService.getAcademicYears(apiToken, activeOrg)
    setAllGroups(parsedAcademicYears || [])
  }


  return (
     <ResponsiveContext.Consumer>
      {(size) => {
        return (
      <>
        <BreadcrumbBar
          title="Dashboard"
          path={<> <Anchor href="/">BehaviourSmart</Anchor></>}
        />

        <DashboardStats />

        <Box
          direction={
            ['xsmall','small','medium'].includes(size) ? 'column' : 'row-responsive'
          }
          gap="medium" pad={{bottom: 'medium'}}>
          <Box
              direction="column"
              width="large"
              gap="medium"
          >

          <Box
              background="white"
              direction="column"
              gap="small"
              flex="grow"
              pad="medium"
              round="small"
              width="large"
              justify="start"
            >
              <Text weight="bold">Incidents I'm collaborating on</Text>
                {collaboratingReports?.length > 0
                  ? collaboratingReports.map((report) => (
                    <Box direction="row" key={report.latestSubmission} gap="small">
                      <Box style={{ width: '90%' }}>
                        <ActionButton
                            margin={{ top: 'small' }}
                            href={`/incident/${report.subType === 'highlevel' ? 'high-level/' : '' }${report.subject}/edit/${report.latestSubmission}`}
                            label={`[${moment(report.dateOfIncident).format('DD/MM/YYYY')}] ${report.authorFirstname} ${report.authorLastname} - ${report.studentFirstname} ${report.studentLastname}`}
                          />
                      </Box>
                      <Box style={{
                        width: size === 'small' ? '10%' : 'auto'
                      }} align="end">
                        <ActionButton
                          margin={{ top: 'small' }}
                          // sx={{ width: '10%', display: 'flex' }}
                          onClick={() => {
                            ReportService.acknowledgeCollaborator(
                              apiToken,
                              report.id
                            ).then(() => {
                              getCollaboratingReports();
                            });
                          }}
                          label={'X'}
                        />
                      </Box>
                    </Box>
                    ))
                  : 'No incidents to display'}
            </Box>
          </Box>
          <Box
            // background="white"
            direction="column"
            gap="medium"
            // flex="grow"
            // pad="none"
            // round="small"
            width="large"
          >

            <Box
              background="white"
              direction="column"
              // wrap
              gap="small"
              flex="grow"
              pad="medium"
              round="small"
              width="large"
              justify="start"
            >
              <Text weight="bold">Year Groups</Text>
                {allGroups?.length > 0 ? allGroups.map((group) => (
                    <Box direction="row" key={group} gap="none">
                      <Box style={{ width: '100%' }}>
                        <ActionButton
                            margin={{ top: 'small' }}
                            href={`/serviceusers/year/${group}`}
                            label={`${group}`}
                          />
                      </Box>
                    </Box>
                  )) : 'No groups to display'}
            </Box>
            <Box
              background="white"
              direction="column"
              gap="medium"
              flex="grow"
              pad="medium"
              round="small"
              width="large"
            >
              <Text weight="bold">
                Welcome to <strong>Be</strong>haviour <strong>Smart</strong>
              </Text>
              <Text>
                <strong>Be</strong>haviour <strong>Smart</strong> is a simple and
                effective way of recording and analysing behaviour in your service.
              </Text>
              <Text>
                Learn how to record an incident, create effective Behaviour Plans
                (Smart Plans), add users or individuals, get the most from the
                Behaviour Smart Analytics system and much more. Our Help Centre is designed to help you get the best out of Behaviour Smart. In it, you will find useful video tutorials and PDF User Guides.{' '}
              </Text>
              <ActionButton
                margin={{ top: 'medium' }}
                href="https://behavioursmart.co.uk/behaviour-smart-help-centre-page"
                label="Help Centre"
              />
            </Box>
            <Box
              background="white"
              direction="column"
              gap="medium"
              flex="grow"
              pad="medium"
              round="small"
              width="large"
            >
              <Text weight="bold">Need Help?</Text>
              <Text>
                {' '}
                Have a question about the system or need technical support?
              </Text>
              <ActionButton
                margin={{ top: 'medium' }}
                style={{ position: 'relative'}}
                href="https://behavioursmart.co.uk/behaviour-smart-support-ticket"
                label="Open Ticket"
                target="_blank"
              />
            </Box>
          </Box>
        </Box>
      </>
    )}}
    </ResponsiveContext.Consumer>
  );
}

export default Dashboard;
